import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { AritoFormV2, BottomActionBarV2, AritoIcon } from '@/components/custom/erp';
import { initialSearchValues, SearchFormValues, searchSchema } from '../../schema';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchFieldActions, SearchFieldState } from '../../hooks';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import type { PhieuChiSearchFilters } from '../../types';
import BasicInfoTab from './BasicInfoTab';
import FilterTab from './FilterTab';
import DetailTab from './DetailTab';

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (filters: PhieuChiSearchFilters) => void;
  formState: {
    state: SearchFieldState;
    actions: SearchFieldActions;
  };
}

const SearchDialog = ({ open, onClose, onSearch, formState }: SearchDialogProps) => {
  const { contentZIndex, overlayZIndex } = useDialogStack(open);

  const handleSubmit = (data: SearchFormValues) => {
    onSearch({
      ...data,
      ma_kh: formState.state.khachHang?.uuid,
      tk: formState.state.taiKhoanCo?.uuid,
      tk_no: formState.state.taikhoanNo?.uuid,
      ma_unit: formState.state.donVi?.uuid
    });
    onClose();
  };

  return (
    <Dialog modal={true} open={open} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 bg-black/50' style={{ zIndex: overlayZIndex }} />
        <div className='fixed inset-0 flex items-center justify-center p-4' style={{ zIndex: contentZIndex }}>
          <DialogPrimitive.Content
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            onPointerDownOutside={event => event.preventDefault()}
            onFocusOutside={event => event.preventDefault()}
            className='flex max-h-[85vh] w-[800px] flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none'
          >
            <div className='flex h-11 min-h-11 items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[16px] font-bold text-black'>
                <AritoIcon icon={12} className='mx-2' />
                <span>Lọc phiếu chi tiền</span>
              </DialogTitle>
              <button
                type='button'
                onClick={onClose}
                className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                title='Đóng'
              >
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
              </button>
            </div>
            <div className='min-h-0 min-w-0 flex-1 overflow-y-auto'>
              <AritoFormV2
                mode='add'
                hasAritoActionBar={false}
                schema={searchSchema}
                onSubmit={handleSubmit}
                initialData={initialSearchValues}
                headerFields={
                  <div className='max-h-[calc(100vh-250px)] overflow-y-auto'>
                    <BasicInfoTab formState={formState} />

                    <Tabs defaultValue='detail' className='w-full'>
                      <TabsList className='h-8 w-full justify-start rounded-none border-b border-[#d7d7d7] p-0 text-slate-600'>
                        <TabsTrigger
                          value='detail'
                          className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                        >
                          Chi tiết
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value='detail' className='mt-0 outline-none'>
                        <DetailTab formState={formState} />
                      </TabsContent>
                    </Tabs>

                    <FilterTab formMode='add' />
                  </div>
                }
                bottomBar={<BottomActionBarV2 mode='add' onClose={onClose} />}
              />
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default SearchDialog;
